import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Send, Users, UserPlus, Tag, ArrowLeft, Search } from 'lucide-react'
import { useUserStore, Contact } from '../stores/userStore'
import toast from 'react-hot-toast'

interface GroupChatMessage {
  id: string
  type: 'user' | 'assistant'
  content: string
  brief_answer?: string
  detailed_description?: string
  timestamp: Date
  taggedContacts?: Contact[]
  query?: string
  isLoading?: boolean
}

interface GroupChatInterfaceProps {
  isOpen: boolean
  onClose: () => void
  onSendMessage: (message: string, taggedContacts: Contact[]) => Promise<void>
  messages: GroupChatMessage[]
  isLoading: boolean
  title?: string
  subtitle?: string
  placeholder?: string
}

const GroupChatInterface: React.FC<GroupChatInterfaceProps> = ({
  isOpen,
  onClose,
  onSendMessage,
  messages,
  isLoading,
  // title = "Group Astrology Chat",
  // subtitle = "Ask compatibility questions and tag your contacts",
  // placeholder = "Type your message and tag contacts with @..."
}) => {
  const { user, addContact } = useUserStore()
  const [inputMessage, setInputMessage] = useState('')
  const [showContactForm, setShowContactForm] = useState(false)
  const [showContacts, setShowContacts] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [newContact, setNewContact] = useState({
    name: '',
    relationship: '',
    day: 1,
    month: 1,
    year: 1990,
    hour: 12,
    minute: 0,
    latitude: 0,
    longitude: 0,
    timezone: 0,
    place_of_birth: ''
  })
  const [taggedContacts, setTaggedContacts] = useState<Contact[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return

    const message = inputMessage
    const contacts = taggedContacts

    setInputMessage('')
    setTaggedContacts([])

    await onSendMessage(message, contacts)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const addNewContact = () => {
    if (!newContact.name || !newContact.relationship) {
      toast.error('Please fill in name and relationship')
      return
    }

    addContact({
      name: newContact.name,
      relationship: newContact.relationship,
      birthData: {
        day: newContact.day,
        month: newContact.month,
        year: newContact.year,
        hour: newContact.hour,
        minute: newContact.minute,
        latitude: newContact.latitude,
        longitude: newContact.longitude,
        timezone: newContact.timezone,
        place_of_birth: newContact.place_of_birth
      }
    })

    setNewContact({
      name: '',
      relationship: '',
      day: 1,
      month: 1,
      year: 1990,
      hour: 12,
      minute: 0,
      latitude: 0,
      longitude: 0,
      timezone: 0,
      place_of_birth: ''
    })
    setShowContactForm(false)
    toast.success('Contact added successfully!')
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputMessage(value)

    // Check for @ mentions
    const mentions = value.match(/@(\w+)/g)
    if (mentions) {
      const mentionedNames = mentions.map(m => m.slice(1))
      const foundContacts = user?.contacts?.filter(contact =>
        mentionedNames.some(name => 
          contact.name.toLowerCase().includes(name.toLowerCase())
        )
      ) || []
      setTaggedContacts(foundContacts)
    } else {
      setTaggedContacts([])
    }
  }

  const suggestedQuestions = [
    "Can I do business with @John?",
    "Are @Sarah and I compatible for marriage?",
    "What's the compatibility between @Mike and @Lisa?",
    "Should I partner with @David for this project?",
    "How compatible are @Emma and I for friendship?"
  ]

  const filteredContacts = user?.contacts?.filter(contact =>
    contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.relationship.toLowerCase().includes(searchQuery.toLowerCase())
  ) || []

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-900 to-blue-900 border-b border-white/20">
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 text-white" />
                </button>
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Compatibility Chat</h2>
                  <p className="text-sm text-gray-300">Ask about relationships & tag contacts</p>
                </div>
              </div>
              <button
                onClick={() => setShowContacts(!showContacts)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <Users className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex min-h-0">
              {/* Contacts Sidebar - Mobile optimized */}
              <AnimatePresence>
                {showContacts && (
                  <motion.div
                    initial={{ x: '-100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '-100%' }}
                    className="absolute inset-y-0 left-0 w-80 bg-gradient-to-br from-purple-900 to-blue-900 border-r border-white/20 z-10 flex flex-col"
                  >
                    <div className="p-4 border-b border-white/20">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-white">Contacts</h3>
                        <button
                          onClick={() => setShowContactForm(true)}
                          className="p-2 bg-blue-600 hover:bg-blue-700 rounded-full transition-colors"
                        >
                          <UserPlus className="w-4 h-4 text-white" />
                        </button>
                      </div>
                      
                      {/* Search */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search contacts..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 text-sm"
                        />
                      </div>
                    </div>

                    {/* Contact List */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-2">
                      {filteredContacts.map((contact) => (
                        <div
                          key={contact.id}
                          className="bg-white/10 rounded-xl p-3 cursor-pointer hover:bg-white/20 transition-colors active:scale-95"
                          onClick={() => {
                            const newMessage = inputMessage + (inputMessage.endsWith(' ') ? '' : ' ') + `@${contact.name} `
                            setInputMessage(newMessage)
                            setShowContacts(false)
                          }}
                        >
                          <div className="font-medium text-white text-sm">{contact.name}</div>
                          <div className="text-xs text-gray-300">{contact.relationship}</div>
                          {contact.birthData && (
                            <div className="text-xs text-gray-400 mt-1">
                              {contact.birthData.day}/{contact.birthData.month}/{contact.birthData.year}
                            </div>
                          )}
                        </div>
                      ))}
                      {filteredContacts.length === 0 && (
                        <div className="text-gray-400 text-center py-6 text-sm">
                          <div className="mb-2">✨ No contacts found</div>
                          <div>Add people to start compatibility analysis!</div>
                        </div>
                      )}
                    </div>

                    {/* Suggested Questions */}
                    <div className="p-4 border-t border-white/20">
                      <h4 className="text-sm font-medium text-white mb-3">Quick Questions:</h4>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {suggestedQuestions.map((question, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              setInputMessage(question)
                              setShowContacts(false)
                            }}
                            className="text-left text-xs text-gray-300 hover:text-white transition-colors p-2 rounded-lg bg-white/5 hover:bg-white/10 w-full active:scale-95"
                          >
                            {question}
                          </button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Chat Area */}
              <div className="flex-1 flex flex-col min-h-0">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.map((message) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-2xl p-3 ${
                          message.type === 'user'
                            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                            : 'bg-white/10 text-white border border-white/20'
                        }`}
                      >
                        {message.taggedContacts && message.taggedContacts.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {message.taggedContacts.map((contact) => (
                              <span
                                key={contact.id}
                                className="inline-flex items-center gap-1 bg-purple-600 text-white text-xs px-2 py-1 rounded-full"
                              >
                                <Tag className="w-3 h-3" />
                                {contact.name}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                          {message.isLoading ? (
                            <div className="flex items-center space-x-2">
                              <span>{message.content}</span>
                              <div className="flex space-x-1">
                                <div className="w-2 h-2 bg-purple-300 rounded-full animate-bounce" />
                                <div className="w-2 h-2 bg-purple-300 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                                <div className="w-2 h-2 bg-purple-300 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                              </div>
                            </div>
                          ) : (
                            <>
                              {message.brief_answer && (
                                <div className="text-sm font-semibold text-purple-300 mb-3 border-l-2 border-purple-400 pl-3 bg-purple-900/20 p-2 rounded">
                                  💫 <strong>Quick Answer:</strong> {message.brief_answer}
                                </div>
                              )}
                              <div className="prose prose-invert prose-sm max-w-none">
                                {message.detailed_description || message.content}
                              </div>
                            </>
                          )}
                        </div>
                        <div className="text-xs opacity-70 mt-2">
                          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                  {isLoading && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex justify-start"
                    >
                      <div className="bg-white/10 text-white rounded-2xl p-3 border border-white/20">
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span className="text-sm">Analyzing compatibility...</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="p-4 border-t border-white/20 bg-gradient-to-r from-purple-900/50 to-blue-900/50">
                  {/* Tagged Contacts Display */}
                  {taggedContacts.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {taggedContacts.map((contact) => (
                        <span
                          key={contact.id}
                          className="inline-flex items-center gap-1 bg-purple-600 text-white text-xs px-2 py-1 rounded-full"
                        >
                          <Tag className="w-3 h-3" />
                          {contact.name}
                          <button
                            onClick={() => {
                              const newContacts = taggedContacts.filter(c => c.id !== contact.id)
                              setTaggedContacts(newContacts)
                              setInputMessage(inputMessage.replace(new RegExp(`@${contact.name}\\s*`, 'g'), ''))
                            }}
                            className="ml-1 hover:text-red-300"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={inputMessage}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask about compatibility..."
                      className="flex-1 bg-white/10 text-white placeholder-gray-400 rounded-2xl px-4 py-3 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-base border border-white/20"
                      disabled={isLoading}
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={isLoading || !inputMessage.trim()}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:bg-gray-600 text-white rounded-2xl px-4 py-3 transition-colors flex items-center justify-center active:scale-95 min-w-[48px]"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Add Contact Modal */}
          <AnimatePresence>
            {showContactForm && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
                onClick={() => setShowContactForm(false)}
              >
                <motion.div
                  initial={{ y: '100%', opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: '100%', opacity: 0 }}
                  className="bg-gradient-to-br from-purple-900 to-blue-900 rounded-t-3xl sm:rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="text-xl font-bold text-white mb-4">Add New Contact</h3>
                  
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Name"
                      value={newContact.name}
                      onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full bg-white/10 text-white placeholder-gray-400 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-base border border-white/20"
                    />
                    
                    <input
                      type="text"
                      placeholder="Relationship (friend, family, colleague, etc.)"
                      value={newContact.relationship}
                      onChange={(e) => setNewContact(prev => ({ ...prev, relationship: e.target.value }))}
                      className="w-full bg-white/10 text-white placeholder-gray-400 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-base border border-white/20"
                    />
                    
                    <div className="grid grid-cols-3 gap-3">
                      <input
                        type="number"
                        placeholder="Day"
                        value={newContact.day}
                        onChange={(e) => setNewContact(prev => ({ ...prev, day: parseInt(e.target.value) || 1 }))}
                        className="bg-white/10 text-white placeholder-gray-400 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-base border border-white/20"
                      />
                      <input
                        type="number"
                        placeholder="Month"
                        value={newContact.month}
                        onChange={(e) => setNewContact(prev => ({ ...prev, month: parseInt(e.target.value) || 1 }))}
                        className="bg-white/10 text-white placeholder-gray-400 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-base border border-white/20"
                      />
                      <input
                        type="number"
                        placeholder="Year"
                        value={newContact.year}
                        onChange={(e) => setNewContact(prev => ({ ...prev, year: parseInt(e.target.value) || 1990 }))}
                        className="bg-white/10 text-white placeholder-gray-400 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-base border border-white/20"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="number"
                        placeholder="Hour (optional)"
                        value={newContact.hour}
                        onChange={(e) => setNewContact(prev => ({ ...prev, hour: parseInt(e.target.value) || 12 }))}
                        className="bg-white/10 text-white placeholder-gray-400 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-base border border-white/20"
                      />
                      <input
                        type="number"
                        placeholder="Minute (optional)"
                        value={newContact.minute}
                        onChange={(e) => setNewContact(prev => ({ ...prev, minute: parseInt(e.target.value) || 0 }))}
                        className="bg-white/10 text-white placeholder-gray-400 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-base border border-white/20"
                      />
                    </div>
                    
                    <input
                      type="text"
                      placeholder="Place of Birth (optional)"
                      value={newContact.place_of_birth}
                      onChange={(e) => setNewContact(prev => ({ ...prev, place_of_birth: e.target.value }))}
                      className="w-full bg-white/10 text-white placeholder-gray-400 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-base border border-white/20"
                    />
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => setShowContactForm(false)}
                      className="flex-1 bg-gray-600 hover:bg-gray-700 text-white rounded-xl px-4 py-3 transition-colors text-base active:scale-95"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={addNewContact}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-4 py-3 transition-colors text-base active:scale-95"
                    >
                      Add Contact
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default GroupChatInterface 